/**
 * GBI bestsellers hero — upright orbit + clickable showcase cycle.
 * Products stay right-way-up via rotate → translate → rotate(-a).
 * Showcase swaps only after preload + decode to avoid previous-frame flicker.
 */
(function () {
  function parseProducts(root) {
    var el = root.querySelector('[data-gbi-bestsellers-json]');
    if (!el) return [];
    try {
      var data = JSON.parse(el.textContent || '[]');
      return Array.isArray(data) ? data.filter(function (p) { return p && p.image && p.url; }) : [];
    } catch (e) {
      return [];
    }
  }

  function preloadImage(p) {
    return new Promise(function (resolve, reject) {
      if (!p || !p.image) {
        reject(new Error('missing image'));
        return;
      }
      var loader = new Image();
      if (p.imageSrcset) loader.srcset = p.imageSrcset;
      loader.onload = function () { resolve(loader); };
      loader.onerror = function () { reject(new Error('image load failed')); };
      loader.src = p.image;
    });
  }

  function waitForFadeOut(img) {
    return new Promise(function (resolve) {
      if (!img.classList.contains('is-in')) {
        resolve();
        return;
      }
      var done = false;
      var finish = function () {
        if (done) return;
        done = true;
        img.removeEventListener('transitionend', onEnd);
        resolve();
      };
      var onEnd = function (e) {
        if (e.propertyName === 'opacity') finish();
      };
      img.addEventListener('transitionend', onEnd);
      img.classList.remove('is-in');
      window.setTimeout(finish, 420);
    });
  }

  function shuffle(arr) {
    var a = arr.slice();
    for (var i = a.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var t = a[i];
      a[i] = a[j];
      a[j] = t;
    }
    return a;
  }

  function initOrbit(stage) {
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var mobile = window.matchMedia('(max-width: 749px)').matches;
    var orbit = stage.querySelector('[data-gbi-orbit]');
    var items = orbit ? Array.prototype.slice.call(orbit.querySelectorAll('[data-gbi-orbit-item]')) : [];

    if (!orbit || !items.length || reduce || mobile) {
      if (orbit) orbit.setAttribute('data-static', 'true');
      items.forEach(function (el, i) {
        var n = Math.min(items.length, 6);
        if (i >= n) {
          el.style.display = 'none';
          return;
        }
        var maxSpread = Math.max(stage.clientWidth * 0.38, 80);
        var spread = (i - (n - 1) / 2) * Math.min(48, maxSpread / Math.max(n - 1, 1));
        el.style.transform = 'translate(' + spread + 'px, ' + (Math.abs(spread) * 0.06) + 'px)';
      });
      return null;
    }

    function radii() {
      // Wide ellipse: fill stage horizontally, stay inside vertically
      var w = stage.clientWidth;
      var h = stage.clientHeight;
      var itemPad = 44;
      var rxOuter = Math.max(w * 0.48 - itemPad, 160);
      var ryOuter = Math.max(h * 0.40 - itemPad, 110);
      // Keep an open centre so the featured card is not buried
      var rxInner = Math.max(rxOuter * 0.58, 170);
      var ryInner = Math.max(ryOuter * 0.58, 130);
      return {
        rx: [rxInner, (rxInner + rxOuter) * 0.5, rxOuter],
        ry: [ryInner, (ryInner + ryOuter) * 0.5, ryOuter]
      };
    }

    var angle = 0;
    var running = true;
    var last = performance.now();

    function tick(now) {
      if (!running) return;
      if (document.hidden) {
        last = now;
        requestAnimationFrame(tick);
        return;
      }
      var dt = Math.min(32, now - last);
      last = now;
      var R = radii();
      items.forEach(function (el) {
        var ring = Number(el.getAttribute('data-ring') || 0);
        var base = Number(el.getAttribute('data-base') || 0);
        var a = base + angle;
        var rad = (a * Math.PI) / 180;
        var x = Math.cos(rad) * R.rx[ring];
        var y = Math.sin(rad) * R.ry[ring];
        el.style.transform = 'translate(' + x + 'px, ' + y + 'px)';
      });
      // ~7.2 deg/sec at 60fps
      angle += 0.12 * (dt / 16.67);
      requestAnimationFrame(tick);
    }

    requestAnimationFrame(tick);

    var onResize = function () {
      if (window.matchMedia('(max-width: 749px)').matches) {
        running = false;
        window.removeEventListener('resize', onResize);
        initOrbit(stage);
      }
    };
    window.addEventListener('resize', onResize, { passive: true });

    return function stop() {
      running = false;
      window.removeEventListener('resize', onResize);
    };
  }

  function initFeatured(stage, products) {
    if (!products.length) return;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var link = stage.querySelector('[data-gbi-featured-link]');
    var img = stage.querySelector('[data-gbi-featured-img]');
    var name = stage.querySelector('[data-gbi-featured-name]');
    var price = stage.querySelector('[data-gbi-featured-price]');
    var glass = stage.querySelector('.gbi-hero__glass');
    if (!link || !img || !name || !price) return;

    // Keep collection order for first featured card (matches SSR), then cycle
    var list = products.slice();
    var idx = 0;
    var busy = false;
    var timer = null;
    var generation = 0;

    function applyMeta(p) {
      name.textContent = p.title;
      price.textContent = p.price;
      link.href = p.url;
      link.setAttribute('aria-label', 'View ' + p.title);
    }

    function reveal(p, gen) {
      if (gen !== generation) return Promise.resolve();
      img.alt = p.title;
      img.src = p.image;
      if (p.imageSrcset) {
        img.setAttribute('srcset', p.imageSrcset);
      } else {
        img.removeAttribute('srcset');
      }
      applyMeta(p);
      var decodeReady = img.decode
        ? img.decode().catch(function () {})
        : Promise.resolve();
      return decodeReady.then(function () {
        if (gen !== generation) return;
        requestAnimationFrame(function () {
          if (gen !== generation) return;
          img.classList.add('is-in');
          if (glass) glass.classList.add('is-in');
        });
      });
    }

    function show(i, options) {
      options = options || {};
      var p = list[i];
      if (!p) return Promise.resolve();
      if (busy && !options.force) return Promise.resolve();
      busy = true;
      var gen = ++generation;

      var start = options.instant
        ? Promise.resolve()
        : waitForFadeOut(img);

      if (glass && !options.instant) glass.classList.remove('is-in');

      return start
        .then(function () {
          if (gen !== generation) return;
          return preloadImage(p).then(
            function () { return reveal(p, gen); },
            function () { return reveal(p, gen); }
          );
        })
        .then(function () {
          if (gen === generation) busy = false;
          var next = list[(i + 1) % list.length];
          if (next && next !== p) preloadImage(next).catch(function () {});
        })
        .catch(function () {
          if (gen === generation) busy = false;
        });
    }

    // Keep SSR featured product for first paint (no shuffle flash)
    show(0, { instant: true, force: true });

    if (reduce || list.length < 2) return;

    timer = window.setInterval(function () {
      if (busy || document.hidden) return;
      idx = (idx + 1) % list.length;
      show(idx);
    }, 3200);

    // Pause when tab hidden
    document.addEventListener('visibilitychange', function () {
      if (document.hidden && timer) {
        window.clearInterval(timer);
        timer = null;
      } else if (!document.hidden && !timer && list.length > 1 && !reduce) {
        timer = window.setInterval(function () {
          if (busy || document.hidden) return;
          idx = (idx + 1) % list.length;
          show(idx);
        }, 3200);
      }
    });
  }

  function boot() {
    document.querySelectorAll('[data-gbi-bestsellers-hero]').forEach(function (section) {
      var stage = section.querySelector('[data-gbi-orbit-stage]');
      if (!stage || stage.getAttribute('data-gbi-ready')) return;
      stage.setAttribute('data-gbi-ready', '1');
      var products = parseProducts(stage);
      initOrbit(stage);
      initFeatured(stage, products);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
